/**
 * @velyn-custom — vely-pdp-technical-features.js
 * PDF links inside rich-text values download in the same tab (no new tab).
 */
(() => {
  if (customElements.get('vely-tech-features')) return;

  const PDF_PATH = /\.pdf$/i;

  class VelyTechFeatures extends HTMLElement {
    connectedCallback() {
      if (this._initStarted) return;
      this._initStarted = true;
      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);
      this._preparePdfLinks();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this._initStarted = false;
    }

    _preparePdfLinks() {
      this.querySelectorAll('.vely-tech-features__value a[href]').forEach((link) => {
        if (!this._isPdf(link.getAttribute('href'))) return;
        link.setAttribute('download', this._filename(link.href));
        link.setAttribute('target', '_self');
      });
    }

    _onClick(event) {
      const link = event.target.closest('a[href]');
      if (!link || !this.contains(link)) return;
      if (!link.closest('.vely-tech-features__value')) return;
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!this._isPdf(link.getAttribute('href'))) return;

      event.preventDefault();
      this._download(link);
    }

    _isPdf(href) {
      if (!href) return false;
      const raw = href.trim();
      if (!raw || raw.startsWith('#')) return false;

      try {
        return PDF_PATH.test(new URL(raw, window.location.href).pathname);
      } catch {
        return /\.pdf($|[?#])/i.test(raw);
      }
    }

    _filename(href) {
      try {
        const name = decodeURIComponent(new URL(href, window.location.href).pathname.split('/').pop() || '');
        return name || 'download.pdf';
      } catch {
        return 'download.pdf';
      }
    }

    async _download(link) {
      if (link.dataset.velyDownloading === 'true') return;

      const href = link.href;
      const filename = this._filename(href);
      link.dataset.velyDownloading = 'true';

      try {
        const response = await fetch(href, { mode: 'cors', credentials: 'omit' });
        if (!response.ok) throw new Error('pdf-fetch-failed');
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        this._clickDownload(objectUrl, filename);
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
      } catch {
        this._clickDownload(href, filename);
      } finally {
        delete link.dataset.velyDownloading;
      }
    }

    _clickDownload(href, filename) {
      const trigger = document.createElement('a');
      trigger.href = href;
      trigger.download = filename;
      trigger.rel = 'noopener';
      trigger.style.display = 'none';
      document.body.appendChild(trigger);
      trigger.click();
      trigger.remove();
    }
  }

  customElements.define('vely-tech-features', VelyTechFeatures);
})();
